import type { VectorPlan } from '@elec/plan';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import type { Id, Locale, Point } from '@elec/domain';
import type { Editor } from '@elec/editor';
import { paperSize } from '@elec/layout';
import { controlInfo } from '@elec/analysis';
import type { Project, ProjectDiff } from '@elec/domain';
import { wirePolyline, type RenderDiff } from '@elec/renderer';
import { Canvas2DRenderer, DARK_THEME, LIGHT_THEME, Scene, Viewport, type RenderStats } from '@elec/renderer';

export interface BenchResult {
  frames: number;
  fps: number;
  avgMs: number;
  p95Ms: number;
  maxMs: number;
  avgRenderMs: number;
}

export interface CanvasHandle {
  fit(): void;
  fitPage(): void;
  benchmark(durationMs?: number): Promise<BenchResult>;
  /** Last known pointer position in sheet coordinates. */
  cursor(): Point;
  /** Sheet coordinates → client (page) pixels; used by end-to-end tests. */
  toClient(x: number, y: number): Point;
  /** Zoom to the given elements (on the current sheet). */
  zoomTo(ids: Id[]): void;
  /** Zoom to an area of the sheet, in millimetres. */
  zoomToArea(b: { minX: number; minY: number; maxX: number; maxY: number }): void;
  /** Centre of the visible area, in sheet millimetres. */
  viewCenter(): Point;
}

interface Props {
  editor: Editor;
  onZoom(scale: number): void;
  /** Double-click on an element in select mode (focus the inspector). */
  onEditElement(id: Id): void;
  label: string;
  /** Decoded plan backgrounds by asset id. */
  images: ReadonlyMap<string, CanvasImageSource | VectorPlan>;
  /** Language of the drawing's texts (the project's). */
  locale: Locale;
  /** Revision comparison to show. */
  diff?: ProjectDiff;
  /** Remarks pinned on the drawings (inspection body), for a sheet. */
  pins?: (
    sheetId: string,
  ) => Array<{ x: number; y: number; label: string; done?: boolean; active?: boolean }>;
  /** When set, a click picks a point on the sheet (pinning a remark) instead of editing. */
  onPick?: (p: { x: number; y: number }) => void;
  /** Real-time cursors of a shared project: others' pointers to draw, ours to send. */
  peers?: {
    list(sheetId: string): Array<{ x: number; y: number; name: string; color: string }>;
    move(sheetId: string | null, x?: number, y?: number): void;
    subscribe(fn: () => void): () => void;
  };
}

const diffCache = new WeakMap<ProjectDiff, Map<string, RenderDiff>>();
/** The part of a revision comparison on one sheet, with removed wires as polylines. */
function sheetDiff(d: ProjectDiff, sheetId: string): RenderDiff {
  let m = diffCache.get(d);
  if (!m) diffCache.set(d, (m = new Map()));
  let r = m.get(sheetId);
  if (!r) {
    const before = { ...d.before, revisions: {} } as Project;
    r = {
      added: d.added,
      changed: d.changed,
      removedElements: d.removedElements.filter((e) => e.sheetId === sheetId),
      removedWires: d.removedWires
        .filter((w) => w.sheetId === sheetId)
        .map((w) => wirePolyline(before, w) ?? []),
    };
    m.set(sheetId, r);
  }
  return r;
}

/** Trackpads send small, fractional or horizontal deltas; mouse wheels send coarse vertical steps. */
function isTrackpadPan(e: WheelEvent): boolean {
  if (e.ctrlKey || e.metaKey) return false; // pinch-zoom arrives as ctrl+wheel
  if (e.deltaMode !== 0) return false;
  return e.deltaX !== 0 || !Number.isInteger(e.deltaY) || Math.abs(e.deltaY) < 30;
}

export const Canvas = forwardRef<CanvasHandle, Props>(function Canvas(
  { editor, onZoom, onEditElement, label, images, locale, diff, peers, pins, onPick },
  ref,
) {
  const peersRef = useRef(peers);
  peersRef.current = peers;
  const pinsRef = useRef(pins);
  pinsRef.current = pins;
  const pickRef = useRef(onPick);
  pickRef.current = onPick;
  const imagesRef = useRef(images);
  imagesRef.current = images;
  const diffRef = useRef(diff);
  diffRef.current = diff;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const vp = useRef(new Viewport(40, 40, 3));
  const renderer = useRef(new Canvas2DRenderer());
  const scene = useRef<Scene | null>(null);
  const hoverScene = useRef<Scene | null>(null);
  const size = useRef({ w: 0, h: 0, dpr: 1 });
  const pan = useRef<{ x: number; y: number } | null>(null);
  const hover = useRef<Id | undefined>(undefined);
  const spaceDown = useRef(false);
  const cursor = useRef<Point>({ x: 0, y: 0 });
  const frame = useRef(0);
  const lastStats = useRef<RenderStats | null>(null);
  const dark = useRef(window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false);
  const cb = useRef({ onZoom, onEditElement });
  cb.current = { onZoom, onEditElement };
  const lastSheet = useRef<Id | null>(null);

  const draw = () => {
    frame.current = 0;
    const c = canvasRef.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    const sheetId = editor.sheetId;
    if (!scene.current || scene.current.sheetId !== sheetId) scene.current = new Scene(sheetId);
    const p = editor.displayProject();
    scene.current.sync(p);
    editor.tolerance = 6 / vp.current.scale;
    lastStats.current = renderer.current.render(
      ctx,
      size.current.w,
      size.current.h,
      size.current.dpr,
      vp.current,
      scene.current,
      p,
      {
        sheet: p.sheets[sheetId],
        selection: editor.selection,
        hover: editor.tool.kind === 'select' ? hover.current : undefined,
        theme: dark.current ? DARK_THEME : LIGHT_THEME,
        overlay: {
          ...editor.overlay(),
          ...(peersRef.current ? { peers: peersRef.current.list(sheetId) } : {}),
          ...(pinsRef.current ? { pins: pinsRef.current(sheetId) } : {}),
        },
        images: imagesRef.current,
        ...(diffRef.current ? { diff: sheetDiff(diffRef.current, sheetId) } : {}),
        // Terminal numbers and cross-references (contacts, off-page connectors).
        ...(p.sheets[sheetId]?.kind !== 'position-plan' ? { annotations: controlInfo(p).annotations } : {}),
      },
    );
  };
  renderer.current.locale = locale;
  // Redraw when a plan image finishes decoding or the language changes.
  useEffect(() => {
    if (!frame.current) frame.current = requestAnimationFrame(draw);
  }, [images, locale, diff, pins]);
  const invalidate = () => {
    if (!frame.current) frame.current = requestAnimationFrame(draw);
  };
  // Others' pointers moved: redraw.
  useEffect(() => peers?.subscribe(invalidate), [peers]);

  const fitPage = () => {
    // displayProject: a previewed proposal may show a sheet that does not exist yet.
    const sheet = editor.displayProject().sheets[editor.sheetId];
    if (!sheet) return;
    const { w, h } = paperSize(sheet);
    vp.current.fit({ minX: 0, minY: 0, maxX: w, maxY: h }, size.current.w, size.current.h, 24);
    cb.current.onZoom(vp.current.scale);
    invalidate();
  };

  const fit = () => {
    if (!scene.current || scene.current.sheetId !== editor.sheetId) scene.current = new Scene(editor.sheetId);
    scene.current.sync(editor.project);
    const b = scene.current.bounds();
    const sheet = editor.project.sheets[editor.sheetId];
    if (!sheet) return;
    const { w, h } = paperSize(sheet);
    // Fit the page, or the drawing if it extends beyond the page.
    const box = b
      ? {
          minX: Math.min(0, b.minX),
          minY: Math.min(0, b.minY),
          maxX: Math.max(w, b.maxX),
          maxY: Math.max(h, b.maxY),
        }
      : { minX: 0, minY: 0, maxX: w, maxY: h };
    vp.current.fit(box, size.current.w, size.current.h, 24);
    cb.current.onZoom(vp.current.scale);
    invalidate();
  };

  // Redraw on every editor or project change; fit the page when switching sheets.
  useEffect(() => {
    const unsub = editor.subscribe(() => {
      if (lastSheet.current !== editor.sheetId) {
        lastSheet.current = editor.sheetId;
        fitPage();
      }
      invalidate();
    });
    return () => {
      unsub();
    };
  }, [editor]);

  // Size the backing store to the element and device pixel ratio.
  useEffect(() => {
    const c = canvasRef.current!;
    let first = true;
    const ro = new ResizeObserver(() => {
      const dpr = window.devicePixelRatio || 1;
      const r = c.getBoundingClientRect();
      size.current = { w: r.width, h: r.height, dpr };
      c.width = Math.round(r.width * dpr);
      c.height = Math.round(r.height * dpr);
      if (first && r.width > 0) {
        first = false;
        lastSheet.current = editor.sheetId;
        fitPage();
      }
      draw();
    });
    ro.observe(c);
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    const onScheme = (e: MediaQueryListEvent) => {
      dark.current = e.matches;
      invalidate();
    };
    mq?.addEventListener('change', onScheme);
    const keyDown = (e: KeyboardEvent) => {
      if (e.key === ' ' && !(e.target as HTMLElement).closest('input, textarea, select')) {
        spaceDown.current = true;
        c.style.cursor = 'grab';
        e.preventDefault();
      }
    };
    const keyUp = (e: KeyboardEvent) => {
      if (e.key === ' ') {
        spaceDown.current = false;
        updateCursor();
      }
    };
    window.addEventListener('keydown', keyDown);
    window.addEventListener('keyup', keyUp);
    return () => {
      ro.disconnect();
      mq?.removeEventListener('change', onScheme);
      window.removeEventListener('keydown', keyDown);
      window.removeEventListener('keyup', keyUp);
    };
  }, [editor]);

  const updateCursor = () => {
    const c = canvasRef.current;
    if (!c) return;
    const t = editor.tool.kind;
    c.style.cursor = pan.current
      ? 'grabbing'
      : spaceDown.current
        ? 'grab'
        : t !== 'select'
          ? 'crosshair'
          : hover.current
            ? 'move'
            : 'default';
  };

  const local = (e: { clientX: number; clientY: number }) => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const toSheet = (e: { clientX: number; clientY: number }) => {
    const s = local(e);
    return vp.current.toSheet(s.x, s.y);
  };
  const mods = (e: React.PointerEvent | React.MouseEvent) => ({
    shift: e.shiftKey,
    alt: e.altKey,
    mod: e.metaKey || e.ctrlKey,
  });

  useImperativeHandle(ref, () => ({
    fit,
    fitPage,
    cursor: () => cursor.current,
    viewCenter: () => ({
      x: (size.current.w / 2 - vp.current.offsetX) / vp.current.scale,
      y: (size.current.h / 2 - vp.current.offsetY) / vp.current.scale,
    }),
    zoomTo: (ids) => {
      if (!scene.current || scene.current.sheetId !== editor.sheetId)
        scene.current = new Scene(editor.sheetId);
      scene.current.sync(editor.project);
      const items = scene.current
        .query({ minX: -1e9, minY: -1e9, maxX: 1e9, maxY: 1e9 })
        .filter((i) => ids.includes(i.id));
      if (!items.length) return;
      const b = {
        minX: Math.min(...items.map((i) => i.minX)) - 15,
        minY: Math.min(...items.map((i) => i.minY)) - 15,
        maxX: Math.max(...items.map((i) => i.maxX)) + 25,
        maxY: Math.max(...items.map((i) => i.maxY)) + 15,
      };
      // Do not zoom in further than ~3× true size, so the context stays visible.
      vp.current.fit(b, size.current.w, size.current.h, 40);
      if (vp.current.scale > 12)
        vp.current.zoomAt(size.current.w / 2, size.current.h / 2, 12 / vp.current.scale);
      cb.current.onZoom(vp.current.scale);
      invalidate();
    },
    zoomToArea: (b) => {
      vp.current.fit(b, size.current.w, size.current.h, 20);
      cb.current.onZoom(vp.current.scale);
      invalidate();
    },
    toClient: (x, y) => {
      const r = canvasRef.current!.getBoundingClientRect();
      const s = vp.current.toScreen(x, y);
      return { x: s.x + r.left, y: s.y + r.top };
    },
    benchmark: (durationMs = 4000) =>
      new Promise<BenchResult>((resolve) => {
        fit();
        const v = vp.current;
        const base = { x: v.offsetX, y: v.offsetY, s: v.scale };
        const deltas: number[] = [];
        const renderTimes: number[] = [];
        const t0 = performance.now();
        let last = t0;
        const step = (now: number) => {
          const t = (now - t0) / 1000;
          // Pan in a circle while zooming in and out between 1× and 6× the fit level.
          const z = base.s * (1 + 2.5 * (1 - Math.cos(t * 1.3)));
          v.scale = z;
          v.offsetX = base.x - Math.sin(t) * 300 - (z - base.s) * 200;
          v.offsetY = base.y - Math.cos(t * 0.7) * 200 - (z - base.s) * 150;
          draw();
          if (lastStats.current) renderTimes.push(lastStats.current.ms);
          deltas.push(now - last);
          last = now;
          if (now - t0 < durationMs) requestAnimationFrame(step);
          else {
            v.offsetX = base.x;
            v.offsetY = base.y;
            v.scale = base.s;
            draw();
            const ds = deltas.slice(1).sort((a, b) => a - b);
            const avg = ds.reduce((a, b) => a + b, 0) / Math.max(ds.length, 1);
            resolve({
              frames: ds.length,
              fps: Math.round(1000 / avg),
              avgMs: +avg.toFixed(1),
              p95Ms: +(ds[Math.floor(ds.length * 0.95)] ?? 0).toFixed(1),
              maxMs: +(ds.at(-1) ?? 0).toFixed(1),
              avgRenderMs: +(
                renderTimes.reduce((a, b) => a + b, 0) / Math.max(renderTimes.length, 1)
              ).toFixed(2),
            });
          }
        };
        requestAnimationFrame(step);
      }),
  }));

  const onPointerDown = (e: React.PointerEvent) => {
    const c = canvasRef.current!;
    c.focus({ preventScroll: true });
    c.setPointerCapture(e.pointerId);
    if (e.button === 1 || (e.button === 0 && spaceDown.current)) {
      pan.current = local(e);
      updateCursor();
      e.preventDefault();
      return;
    }
    if (e.button === 2) {
      // Right click leaves the current tool (or cancels the wire in progress).
      if (editor.tool.kind !== 'select' || editor.isWiring) editor.cancel();
      return;
    }
    if (e.button !== 0) return;
    const p = toSheet(e);
    if (pickRef.current) return pickRef.current(p);
    cursor.current = p;
    editor.pointerDown(p, mods(e));
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (pan.current) {
      const s = local(e);
      vp.current.panBy(s.x - pan.current.x, s.y - pan.current.y);
      pan.current = s;
      invalidate();
      return;
    }
    const p = toSheet(e);
    cursor.current = p;
    peersRef.current?.move(editor.sheetId, p.x, p.y);
    editor.pointerMove(p);
    if (editor.tool.kind === 'select') {
      if (!hoverScene.current || hoverScene.current.sheetId !== editor.sheetId)
        hoverScene.current = new Scene(editor.sheetId);
      hoverScene.current.sync(editor.project);
      const h = hoverScene.current.hitTest(p, 6 / vp.current.scale)?.id;
      if (h !== hover.current) {
        hover.current = h;
        invalidate();
      }
    }
    updateCursor();
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (pan.current) {
      pan.current = null;
      updateCursor();
      return;
    }
    if (e.button === 0 && !pickRef.current) editor.pointerUp(toSheet(e));
  };

  const onDoubleClick = (e: React.MouseEvent) => {
    const p = toSheet(e);
    if (editor.tool.kind === 'wire') editor.doubleClick(p);
    else if (editor.tool.kind === 'select') {
      const [only] = editor.selection;
      if (editor.selection.size === 1 && only && editor.project.elements[only])
        cb.current.onEditElement(only);
    }
  };

  // Wheel needs a non-passive listener to prevent page zoom/scroll.
  useEffect(() => {
    const c = canvasRef.current!;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const s = local(e);
      if (isTrackpadPan(e)) vp.current.panBy(-e.deltaX, -e.deltaY);
      else {
        const factor = Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0015));
        vp.current.zoomAt(s.x, s.y, factor);
        cb.current.onZoom(vp.current.scale);
      }
      invalidate();
    };
    c.addEventListener('wheel', onWheel, { passive: false });
    return () => c.removeEventListener('wheel', onWheel);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="canvas"
      data-testid="canvas"
      tabIndex={0}
      aria-label={label}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerLeave={() => {
        peersRef.current?.move(null);
        if (hover.current) {
          hover.current = undefined;
          invalidate();
        }
      }}
      onDoubleClick={onDoubleClick}
      onContextMenu={(e) => e.preventDefault()}
    />
  );
});
